import type { ThemeTokenSet } from '@ayumia/contracts';
import { resolveTheme, themeScope, type ThemeMode } from '@ayumia/component-runtime';
import { DEFAULT_THEME } from './default-theme';

/**
 * Jetons de thème (59 §12) → variables CSS `--ay-<chemin>` et portée `$theme` des composants. Le
 * renderer ne code aucune couleur : il lit des variables. Sans thème actif, le thème Core par
 * défaut ci-dessous s'applique ; un thème actif le surcharge jeton par jeton.
 */
export { DEFAULT_THEME };

export interface AppliedTheme {
  tokens: Record<string, string | number>;
  variables: Record<string, string>;
  scope: Record<string, unknown>;
}

/**
 * Thème actif puis ses parents (ordre de `GetExperience`) : la chaîne est résolue par le runtime
 * de composants (`extends`), le thème Core par défaut restant la racine de tout.
 */
export function applyThemes(chain: readonly ThemeTokenSet[], mode: ThemeMode = 'default'): AppliedTheme {
  let tokens: Record<string, string | number> = resolveTheme(DEFAULT_THEME, mode);
  let parent: ThemeTokenSet = DEFAULT_THEME;
  for (const theme of [...chain].reverse()) {
    tokens = { ...tokens, ...resolveTheme(theme, mode, parent) };
    parent = { ...theme, primitives: { ...(parent.primitives ?? {}), ...(theme.primitives ?? {}) }, semantic: { ...parent.semantic, ...theme.semantic } };
  }
  const variables: Record<string, string> = {};
  for (const [path, value] of Object.entries(tokens)) variables[cssVariable(path)] = String(value);
  // Noms du design system de référence (design/ayumia-v2-ds, colors_and_type.css) : une feuille du
  // design system se branche telle quelle, sans réécriture, sur les mêmes valeurs.
  for (const [name, paths] of Object.entries(REFERENCE_VARIABLES)) {
    const value = paths.map((path) => tokens[path]).find((candidate) => candidate !== undefined);
    if (value !== undefined) variables[`--${name}`] = String(value);
  }
  return { tokens, variables, scope: themeScope(tokens) };
}

/**
 * Variables de référence du design system AYUMIA v2 → jetons sémantiques (premier présent). Les
 * deux nomenclatures désignent les mêmes valeurs : `--bg` vaut `--ay-color-surface-canvas`.
 */
export const REFERENCE_VARIABLES: Readonly<Record<string, readonly string[]>> = {
  bg: ['color.surface.canvas'],
  surface: ['color.surface.default'],
  'surface-muted': ['color.surface.muted'],
  border: ['color.border.default'],
  'border-strong': ['color.border.strong'],
  ink: ['color.text.primary'],
  'ink-soft': ['color.text.secondary'],
  'ink-muted': ['color.text.muted'],
  accent: ['color.accent.default'],
  'accent-soft': ['color.accent.soft'],
  'accent-ink': ['color.action.primary_hover'],
  sidebar: ['color.shell.rail'],
  'sidebar-ink': ['color.shell.rail_text'],
  'sidebar-active': ['color.shell.rail_active_text'],
  header: ['color.shell.topbar'],
  'header-ink': ['color.shell.topbar_text'],
  logo: ['color.brand.logo', 'color.accent.default'],
  warn: ['color.status.warning'],
  'warn-soft': ['color.status.warning_soft'],
  danger: ['color.status.danger'],
  'danger-soft': ['color.status.danger_soft'],
  success: ['color.status.success'],
  'success-soft': ['color.status.success_soft'],
  'chip-bg': ['color.surface.sunken'],
  'monogram-opacity': ['shell.monogram_opacity'],
  'tone-ok-bg': ['color.tone.ok.bg'],
  'tone-ok-fg': ['color.tone.ok.fg'],
  'tone-warn-bg': ['color.tone.warn.bg'],
  'tone-warn-fg': ['color.tone.warn.fg'],
  'tone-danger-bg': ['color.tone.danger.bg'],
  'tone-danger-fg': ['color.tone.danger.fg'],
  'tone-info-bg': ['color.tone.info.bg'],
  'tone-info-fg': ['color.tone.info.fg'],
  'tone-neutral-bg': ['color.tone.neutral.bg'],
  'tone-neutral-fg': ['color.tone.neutral.fg'],
  'font-body': ['typography.family'],
  'font-display': ['typography.family_display'],
  'font-mono': ['typography.family_mono'],
  'radius-sm': ['radius.sm'],
  radius: ['radius.md'],
  'radius-lg': ['radius.lg'],
};

export function cssVariable(path: string): string {
  return `--ay-${path.replace(/[._]/g, '-')}`;
}

/** Valeur CSS d'un jeton sémantique : `color.text.primary` → `var(--ay-color-text-primary)`. */
export function token(path: string): string {
  return `var(${cssVariable(path)})`;
}
